import {useCallback, useRef} from 'react'

import {
  useSavedViewConfiguration,
  useSavedViewConfigurationActions,
  useSavedViewConfigurationDirtyState,
} from '../../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration, ViewConfigurationOverrides} from '../../contexts/views/view-configuration'
import {SavedViewOptionsMenu} from './SavedViewOptionsMenu'
import {useSavedViewFields} from './use-saved-view-fields'

type BoardPicks = Pick<ViewConfiguration, 'groupBy' | 'swimlaneBy' | 'visibleFields' | 'sorts'>

interface ConnectedSavedViewOptionsMenuProps {
  basePath: string
  disabled?: boolean
  restoreBoardPicks?: boolean
}

/**
 * The view options menu, connected to the saved view configuration and its fields. Each pick
 * applies as it is made.
 *
 * With `restoreBoardPicks`, switching from List back to Board restores the columns, swimlanes, sort
 * and visible fields the board had before the switch, for as long as the menu is mounted and until
 * Discard is picked.
 */
export function ConnectedSavedViewOptionsMenu({
  basePath,
  disabled = false,
  restoreBoardPicks = false,
}: ConnectedSavedViewOptionsMenuProps) {
  const {data: fields, isError, isPending} = useSavedViewFields(basePath)
  const configuration = useSavedViewConfiguration()
  const {applyConfiguration, prefetchConfiguration, reset} = useSavedViewConfigurationActions()
  const {dirty, isConfigurationDirty} = useSavedViewConfigurationDirtyState()

  const boardPicksRef = useRef<BoardPicks | null>(null)
  const withBoardPicks = useCallback(
    (changes: ViewConfigurationOverrides): ViewConfigurationOverrides =>
      changes.layout === 'board' && boardPicksRef.current ? {...boardPicksRef.current, ...changes} : changes,
    [],
  )
  const apply = useCallback(
    (changes: ViewConfigurationOverrides) => {
      if (restoreBoardPicks && changes.layout === 'list') {
        const {groupBy, swimlaneBy, visibleFields, sorts} = configuration
        boardPicksRef.current = {groupBy, swimlaneBy, visibleFields, sorts}
      }
      applyConfiguration(withBoardPicks(changes))
    },
    [applyConfiguration, configuration, restoreBoardPicks, withBoardPicks],
  )
  const intend = useCallback(
    (changes: ViewConfigurationOverrides) => prefetchConfiguration(withBoardPicks(changes)),
    [prefetchConfiguration, withBoardPicks],
  )
  const discard = useCallback(() => {
    boardPicksRef.current = null
    reset()
  }, [reset])

  return (
    <SavedViewOptionsMenu
      fields={fields}
      isPending={isPending}
      isError={isError}
      configuration={configuration}
      disabled={disabled}
      showDirtyIndicator={isConfigurationDirty}
      configurationDirty={dirty}
      onDiscard={discard}
      onApply={apply}
      onIntent={intend}
    />
  )
}
